import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { tokenManager, securityUtils } from '../../services/api';

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/v1/auth/login', { username, password });
      const { access_token, refresh_token, admin } = response.data;
      
      // Store tokens securely
      tokenManager.setToken(access_token);
      if (refresh_token) {
        tokenManager.setRefreshToken(refresh_token);
      }
      
      // Verify token is not expired
      if (securityUtils.isTokenExpired(access_token)) {
        throw new Error('Received expired token');
      }
      
      return { token: access_token, admin };
    } catch (error) {
      // Clear any existing tokens on login failure
      tokenManager.clearTokens();
      return rejectWithValue(
        error.response?.data?.detail || error.message || 'Login failed'
      );
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      // Call logout endpoint if available
      await api.post('/api/v1/auth/logout');
    } catch (error) {
      console.warn('Logout endpoint failed, continuing with local logout:', error);
    } finally {
      // Always clear tokens locally
      tokenManager.clearTokens();
    }
    return null;
  }
);

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const token = tokenManager.getToken();
      if (!token) {
        return rejectWithValue('No token found');
      }
      
      // Validate token before making request
      if (!securityUtils.validateCurrentToken()) {
        return rejectWithValue('Invalid or expired token');
      }
      
      const response = await api.get('/api/v1/auth/me');
      
      return { token, admin: response.data };
    } catch (error) {
      tokenManager.clearTokens();
      return rejectWithValue(
        error.response?.data?.detail || 'Authentication check failed'
      );
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    token: null,
    admin: null,
    isAuthenticated: false,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.admin = action.payload.admin;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.token = null;
        state.admin = null;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.token = null;
        state.admin = null;
        state.error = null;
      })
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.admin = action.payload.admin;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.admin = null;
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
